import { useState } from 'react'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { FieldHint } from '@/components/field-hint'
import { cn } from '@/lib/utils'
import { LeadCard } from './lead-card'
import { KANBAN_COLUMNS, type Lead, type LeadStatus } from './types'
import { useUpdateLead } from './use-leads'

const STAGE_TOOLTIPS: Record<string, string> = {
  new: 'New leads found matching your ICP. Review and add to a campaign.',
  connection_sent: 'Connection request sent. Waiting for acceptance.',
  connected: 'Connection accepted. First DM will be sent automatically.',
  contacted: 'First message sent. Follow-ups are scheduled automatically.',
  followup_1_sent: 'First automated follow-up has been sent. If there is no reply, the next one is scheduled for you.',
  followup_2_sent: 'Second automated follow-up has been sent. The system waits, then sends the final one if there is still no reply.',
  followup_3_sent: 'Final automated follow-up has been sent. No more messages go out unless the lead replies.',
  replied: 'Lead replied to your outreach. Review their response, then drag the card to Meeting Booked once a meeting is agreed. Automated messages keep flowing while the card is here, unless you have messaged this lead yourself (then automation is off for good).',
  meeting_booked: 'You move leads here yourself by dragging a Replied card once a meeting is agreed. That STOPS all automated messages to this lead, because you now own the conversation. Drag to Won or Lost to close out, or back to Replied to let the AI resume (unless you have already messaged the lead yourself, then automation stays off).',
  won: 'You drag leads here yourself when a deal closes. Automation stays STOPPED for this lead. Dragging back to Replied reopens the stage, and the AI resumes only if you never messaged this lead yourself.',
  lost: 'You drag leads here yourself when a lead declines or goes cold. Automation stays STOPPED for this lead. Dragging back to Replied reopens the stage, and the AI resumes only if you never messaged this lead yourself.',
}

/**
 * Stages are advanced by the engine (invites, accepts, DMs, replies) — a
 * human dragging a card can't make LinkedIn accept a connection, and a
 * hand-moved status desyncs the board from what actually happened on the
 * wire. The legitimate human judgment calls are:
 *   Replied → Meeting Booked   (a meeting got agreed in the thread)
 *   Meeting Booked → Won/Lost  (closing out the outcome)
 *   any of those → Replied     (undo / hand the conversation back to the AI)
 * Server-side rule (leads PUT): moving INTO meeting_booked/won/lost pauses
 * every automated message to the lead; moving back to Replied resumes them.
 */
function isDragAllowed(from: LeadStatus, to: LeadStatus): boolean {
  // 'qualified' has no column of its own — those cards render inside the
  // Replied column, so they must be draggable the same way.
  if ((from === 'replied' || from === 'qualified') && to === 'meeting_booked') return true
  if (from === 'meeting_booked' && (to === 'won' || to === 'lost')) return true
  // Undo / resume path: hand the conversation back to the AI.
  if ((from === 'meeting_booked' || from === 'won' || from === 'lost') && to === 'replied') return true
  return false
}

/** Statuses whose cards get a drag handle at all. */
const DRAGGABLE_STATUSES: ReadonlySet<LeadStatus> = new Set(['replied', 'qualified', 'meeting_booked', 'won', 'lost'])

/** Map a lead status to the kanban column that renders it (must mirror getLeadsForColumn). */
function columnForStatus(status: LeadStatus): LeadStatus {
  if (status === 'discovered' || status === 'warmup' || status === 'warmup_done') return 'new'
  if (status === 'dm_sent') return 'contacted'
  if (status === 'qualified') return 'replied'
  return status
}

type KanbanBoardProps = {
  readonly leads: readonly Lead[]
  readonly onLeadClick: (lead: Lead) => void
  readonly selectMode?: boolean
  readonly selectedIds?: ReadonlySet<string>
  readonly onToggleSelect?: (id: string) => void
}

type ColumnProps = {
  readonly id: LeadStatus
  readonly label: string
  readonly headerClass: string
  readonly leads: readonly Lead[]
  readonly onLeadClick: (lead: Lead) => void
  readonly selectMode: boolean
  readonly selectedIds: ReadonlySet<string>
  readonly onToggleSelect?: (id: string) => void
}

function KanbanColumn({ id, label, headerClass, leads, onLeadClick, selectMode, selectedIds, onToggleSelect }: ColumnProps) {
  const leadIds = leads.map((l) => l.id)
  const tooltip = STAGE_TOOLTIPS[id]
  // Register the whole column as a droppable so drops land even when it has
  // no cards. The old code had no column droppables AND set
  // pointer-events-none on empty columns — dropping into an empty
  // Meeting Booked / Won / Lost column was physically impossible.
  const { setNodeRef, isOver } = useDroppable({ id: `col:${id}` })

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex w-64 shrink-0 flex-col overflow-hidden rounded-xl border bg-muted/30 transition-shadow',
        isOver && 'ring-2 ring-[var(--color-cobalt-bright)] ring-inset',
      )}
      style={{ maxHeight: 'calc(100vh - 280px)', minHeight: '200px' }}
    >
      <div className={cn('flex items-center justify-between px-3 py-2 pointer-events-auto shrink-0', headerClass)}>
        <div className="flex items-center gap-1">
          <span className="text-sm font-semibold">{label}</span>
          {tooltip && <FieldHint label={`What is ${label}?`}>{tooltip}</FieldHint>}
        </div>
        <span className="rounded-full bg-[rgba(255,255,255,0.06)] px-2 py-0.5 text-xs font-medium text-[var(--color-silver-bright)]">
          {leads.length}
        </span>
      </div>

      <SortableContext items={leadIds} strategy={verticalListSortingStrategy}>
        <div className="min-h-0 flex-1 overflow-y-auto" data-column-id={id}>
          <div className="flex flex-col gap-2 p-2">
            {leads.map((lead) => (
              <LeadCard
                key={lead.id}
                lead={lead}
                onLeadClick={onLeadClick}
                selectMode={selectMode}
                isSelected={selectedIds.has(lead.id)}
                onToggleSelect={onToggleSelect}
                dragDisabled={!DRAGGABLE_STATUSES.has(lead.status)}
              />
            ))}
            {leads.length === 0 && (
              <div className="flex h-16 items-center justify-center text-xs text-muted-foreground">
                No leads
              </div>
            )}
          </div>
        </div>
      </SortableContext>
    </div>
  )
}

export function KanbanBoard({ leads, onLeadClick, selectMode = false, selectedIds, onToggleSelect }: KanbanBoardProps) {
  const effectiveSelected = selectedIds ?? new Set<string>()
  const [activeLead, setActiveLead] = useState<Lead | null>(null)
  const updateLead = useUpdateLead()

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
  )

  function getLeadsForColumn(status: LeadStatus): readonly Lead[] {
    // Map kanban column status to lead statuses
    if (status === 'new') {
      return leads.filter((l) => l.status === 'new' || l.status === 'discovered' || l.status === 'warmup' || l.status === 'warmup_done')
    }
    if (status === 'connected') {
      return leads.filter((l) => l.status === 'connected')
    }
    if (status === 'contacted') {
      return leads.filter((l) => l.status === 'contacted' || l.status === 'dm_sent')
    }
    if (status === 'replied') {
      // 'qualified' lost its own column but the status still exists in the
      // data model — keep those leads visible instead of vanishing them.
      return leads.filter((l) => l.status === 'replied' || l.status === 'qualified')
    }
    return leads.filter((l) => l.status === status)
  }

  function handleDragStart(event: DragStartEvent) {
    const lead = leads.find((l) => l.id === event.active.id)
    if (lead) setActiveLead(lead)
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveLead(null)
    const { active, over } = event
    if (!over) return

    // Resolve the drop column from dnd-kit's `over` target: either the
    // column droppable itself (`col:<status>`, works for empty columns) or
    // a card, which maps to the column that renders its status. The old
    // elementFromPoint(activatorEvent) hack read the coordinates where the
    // drag STARTED, so it always resolved the SOURCE column — every drop
    // was a self-drop no-op.
    const overId = String(over.id)
    let targetColumnId: LeadStatus | null
    if (overId.startsWith('col:')) {
      targetColumnId = overId.slice(4) as LeadStatus
    } else {
      const overStatus = findLeadColumn(overId, leads)
      targetColumnId = overStatus ? columnForStatus(overStatus) : null
    }

    if (!targetColumnId) return

    const lead = leads.find((l) => l.id === active.id)
    if (!lead || lead.status === targetColumnId) return

    // Defense in depth with the per-card `dragDisabled` — a drop on any
    // column outside the allowed transition is silently ignored.
    if (!isDragAllowed(lead.status, targetColumnId)) return

    updateLead.mutate({ id: String(active.id), updates: { status: targetColumnId } })
  }

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <p className="mb-2 text-xs text-muted-foreground">
        Stages advance automatically as the system works your leads. You can drag:{' '}
        <span className="font-medium text-foreground">Replied → Meeting Booked → Won / Lost</span>.
        Moving a card out of Replied <span className="font-medium text-foreground">pauses all automated messages</span> to
        that lead (you own the conversation). Drag it back to Replied to hand it back to the AI.{' '}
        <span className="font-medium text-foreground">Exception: once you message a lead yourself</span> (from the inbox
        or on LinkedIn), automation for them is off permanently. Dragging the card back to Replied only changes the
        stage, it will never restart messaging.
      </p>
      <div className="flex gap-4 overflow-x-auto pb-4">
        {KANBAN_COLUMNS.map((col) => (
          <KanbanColumn
            key={col.id}
            id={col.id}
            label={col.label}
            headerClass={col.headerClass}
            leads={getLeadsForColumn(col.id)}
            onLeadClick={onLeadClick}
            selectMode={selectMode}
            selectedIds={effectiveSelected}
            onToggleSelect={onToggleSelect}
          />
        ))}
      </div>

      <DragOverlay>
        {activeLead && (
          <div className="rotate-3 opacity-90">
            <LeadCard lead={activeLead} onLeadClick={() => undefined} />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  )
}

function findLeadColumn(leadId: string, leads: readonly Lead[]): LeadStatus | null {
  const lead = leads.find((l) => l.id === leadId)
  return lead?.status ?? null
}
